import type { BaseServiceOptions, Pagination } from '@/core/types'
import {
  TCities,
  TComments,
  TFiles,
  TInvitations,
  TLeads,
  TSellers,
  TStates,
  catalogServiceHandlers,
  moduleServiceHandlers,
  serviceConfig,
} from '../../..'
import { useNotification } from '@/core'
import { useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'

const { model, cities, states, leads, sellers, comments, files } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, enableQuery, filters } = options

  const { errorHttpNotification } = useNotification()

  const loadRecords = useQuery<TInvitations, AxiosError>(
    [model, currentPage, filters?.loadRecords, 'calendar'],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadEstimationComments = useQuery<TComments, AxiosError>(
    [comments, currentPage, filters?.loadEstimationComments, 'estimations'],
    () =>
      moduleServiceHandlers.loadEstimationComments({
        dynamicFilters: filters?.loadEstimationComments,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadEstimationComments,
      onError: (error) => errorHttpNotification(error),
      select: (payload) =>
        payload.map((comment) => {
          const names = comment.user?.split(' ')
          const authorInitials =
            (names?.length ?? 0) >= 2
              ? `${names?.[0].charAt(0)}${names?.[1].charAt(0)}`
              : `${names?.[0].charAt(0)}`

          return {
            ...comment,
            author_initials: authorInitials,
          }
        }),
    },
  )

  const loadSellerComments = useQuery<TComments, AxiosError>(
    [comments, currentPage, filters?.loadSellerComments, 'seller'],
    () =>
      moduleServiceHandlers.loadSellerComments({
        dynamicFilters: filters?.loadSellerComments,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadSellerComments,
      select: (payload) =>
        payload.map((comment) => {
          const names = comment.user?.split(' ')
          const authorInitials =
            (names?.length ?? 0) >= 2
              ? `${names?.[0].charAt(0)}${names?.[1].charAt(0)}`
              : `${names?.[0].charAt(0)}`

          return {
            ...comment,
            author_initials: authorInitials,
          }
        }),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadFiles = useQuery<TFiles, AxiosError>(
    [files, currentPage, filters?.loadFiles],
    () =>
      moduleServiceHandlers.loadFiles({
        dynamicFilters: filters?.loadFiles,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadFiles,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const statesCatalog = useQuery<TStates>(
    [states],
    () => catalogServiceHandlers.statesCatalog.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.statesCatalog,
    },
  )

  const citiesCatalog = useQuery<TCities>(
    [cities, filters?.citiesCatalog],
    () =>
      catalogServiceHandlers.citiesCatalog.loadCatalog({
        dynamicFilters: filters?.citiesCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.citiesCatalog,
    },
  )

  const leadsCatalog = useQuery<TLeads>(
    [leads, filters?.citiesCatalog],
    () =>
      catalogServiceHandlers.leadsCatalog.loadCatalog({
        dynamicFilters: filters?.leadsCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.leadsCatalog,
    },
  )

  const sellersCatalog = useQuery<TSellers>(
    [sellers, filters?.sellersCatalog],
    () =>
      catalogServiceHandlers.sellersCatalog.loadCatalog({
        dynamicFilters: filters?.sellersCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.sellersCatalog,
    },
  )

  return {
    sellersCatalog,
    loadEstimationComments,
    loadSellerComments,
    loadFiles,
    loadRecords,
    statesCatalog,
    citiesCatalog,
    leadsCatalog,
  }
}
